import type { ReactNode } from 'react';
import type moment from 'moment';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from 'recharts';
import { formatDuration } from 'src/utils/formatDuration';
import type { CoAgentAnalyticsRow } from 'src/services/coAgentAnalyticsService';
import { buildAgentChartData } from 'src/pages/AnalyticsPage/AnalyticsAgent/agentChartData';

interface AnalyticsAgentLineChartProps {
  dataKey?: string;
  data?: CoAgentAnalyticsRow[];
  // The request's date_range ('day' labels the x axis by date).
  dateRange?: string;
  timeAxis?: string;
  color?: string;
  // Rows hold a value per member name instead of a team-wide value.
  isByUser?: boolean;
  userColors?: Record<string, string>;
}

// Y-axis title; Recharts clones this element with the Label's props (including `value`).
const YAxisTitle = ({ value, titleX }: { value?: ReactNode; titleX: number }) => (
  <text
    offset="0"
    x="10"
    y="20"
    className="recharts-text fill-ebony text-sm font-semibold capitalize"
    textAnchor="middle"
    fill="#808080"
  >
    <tspan x={titleX} dy="0.71em">
      {value}
    </tspan>
  </text>
);

export const AnalyticsAgentLineChart = ({
  dataKey = '',
  data = [],
  dateRange = 'day',
  timeAxis = 'seconds',
  color = '#65D5DC',
  isByUser = false,
  userColors,
}: AnalyticsAgentLineChartProps) => {
  const isDayRange = dateRange === 'day';
  const { chartData, userNames } = buildAgentChartData(data, dataKey, isByUser);
  const tooltipFormatter = (value: number) => {
    return value ? formatDuration(value, timeAxis as moment.unitOfTime.DurationConstructor) : 0;
  };

  const isSingleDot =
    chartData.filter((charDataItem) => {
      return isByUser ? Object.keys(charDataItem).length > 1 : charDataItem[dataKey];
    }).length <= 1;

  return chartData.length ? (
    <ResponsiveContainer
      width="100%"
      height="100%"
      minHeight={'500px'}
      className={'bg-white rounded-sm-5'}
    >
      <LineChart
        width={500}
        height={300}
        data={chartData}
        margin={{
          top: 50,
          right: 50,
          left: 10,
          bottom: 50,
        }}
      >
        <CartesianGrid vertical={false} strokeDasharray="1 1" />
        <XAxis dataKey="date">
          <Label
            value={isDayRange ? 'Days' : 'Hours'}
            offset={-10}
            position="insideBottomRight"
            className={'fill-ebony text-sm font-semibold'}
          />
        </XAxis>
        <YAxis>
          <Label
            value={timeAxis}
            position="insideTopLeft"
            className={'fill-ebony text-sm font-semibold'}
            content={<YAxisTitle titleX={60} />}
          />
        </YAxis>
        <Tooltip
          formatter={tooltipFormatter}
          labelStyle={{
            color: '#FFF',
            fontFamily: 'Bai Jamjuree',
            fontSize: '14px',
          }}
          contentStyle={{
            borderRadius: '5px',
            background: '#05050D',
          }}
          itemStyle={{
            fontFamily: 'Bai Jamjuree',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 600,
          }}
        />
        {Boolean(data.length) && (
          <ReferenceLine
            x={data[Number.parseInt((data.length / 2).toFixed(0)) - 1]['date']}
            stroke="#CCCCCC"
            strokeDasharray="1 1"
          />
        )}
        {isByUser ? (
          userNames.map((userName) => {
            const stroke = userColors ? userColors[userName] : color;

            return (
              <Line
                type="linear"
                key={userName}
                dataKey={userName}
                name={userName}
                stroke={stroke}
                strokeWidth={3}
                connectNulls={true}
                dot={isSingleDot ? { stroke, strokeWidth: 10 } : false}
              />
            );
          })
        ) : (
          <Line
            type="linear"
            dataKey={dataKey}
            name={dataKey}
            stroke={color}
            strokeWidth={3}
            fontSize={'10px'}
            connectNulls={true}
            dot={isSingleDot ? { stroke: color, strokeWidth: 10 } : false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  ) : (
    <div
      className={
        'min-h-[450px] bg-white rounded-sm-5 flex flex-col items-center justify-center font-medium italic text-ebony_opacity_50 text-lg'
      }
    >
      <p>No data available for the selected period</p>
    </div>
  );
};
